import { useModel as _useModel, mergeModels as _mergeModels, defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, normalizeClass as _normalizeClass, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderSlot as _renderSlot, unref as _unref, mergeProps as _mergeProps } from "vue"

import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { QFile } from 'quasar'
import KUploaderCarousel from './KUploaderCarousel.vue'

/** A stored image's URL, or a newly picked file */
type UploaderImage = string | File
type PlaceholderType = 'avatar' | 'item' | 'bill'

interface ITextObject {
  avatar: { single: string; multiple: string }
  item: { single: string; multiple: string }
  imageHint: string
  errorMessage: string
}

/** KCropper's props, except the image and popup state, which the uploader manages */
interface ICropperOptions {
  /** Width / height of the crop, or NaN for a free ratio */
  aspectRatio?: number
  /** Output size in px; the crop is scaled to fit inside it */
  width?: number
  height?: number
  /** Defaults to PNG (keeping transparency) when accept takes nothing but PNG, and to JPEG otherwise */
  outputType?: 'image/jpeg' | 'image/png'
  /** JPEG only */
  quality?: number
  /** In bytes; a cropped JPEG is compressed to fit it, a cropped PNG is scaled down */
  maxSize?: number
  text?: { title: string; description: string; reset: string; cancel: string; crop: string }
}

interface IProps {
  placeholderType?: PlaceholderType
  placeholderSrc?: string
  /** 1 holds a single image; anything higher holds up to that many, ordered by `sort` */
  maxItem?: number
  /** In MB */
  maxImageSize?: number
  accept?: string
  text?: ITextObject
  deletable?: boolean
  /** Bound to KCropper; options left out fall back to the defaults for placeholder-type */
  cropper?: ICropperOptions
  /** Adds picked images as they are, without opening the cropper */
  noCrop?: boolean
}

interface IEmits {
  (e: 'on-delete'): void
}

interface IState {
  /** The image shown in the preview; kept in range by the images watcher */
  activeIndex: number
  /** The last picked file was rejected (accept or max-image-size) */
  hasError: boolean
  isCropperOpen: boolean
  pickedFile: File | null
  /** A displayable URL per image, in the same order */
  previews: string[]
}

/**
 * The server stores each image under its sort number (sort [3, 1] → 3.jpg shown before 1.jpg) and deletes
 * every stored image missing from sort. A single image is always 1.jpg, and [-1] deletes all of them.
 */
const SINGLE_IMAGE_SORT = 1
const NO_IMAGE_SORT = -1

/** A wildcard like image/* names no format, so {format} lists the common ones it lets through */

export default /*@__PURE__*/_defineComponent({
  ...{ name: 'KUploader' },
  __name: 'KUploader',
  props: /*@__PURE__*/_mergeModels({
    placeholderType: { default: 'avatar' },
    placeholderSrc: { default: '' },
    maxItem: { default: 1 },
    maxImageSize: { default: 0.8 },
    accept: { default: 'image/jpeg, image/jpg' },
    text: { default: () => ({
    avatar: { single: 'Upload Photo', multiple: 'Upload New Photo' },
    item: { single: 'Upload Image', multiple: 'Upload More Images' },
    imageHint: 'Upload {format} image, Max size of {size}',
    errorMessage: 'Invalid Image Format or Size! Please upload in {format} format and under size limit {size}',
  }) },
    deletable: { type: Boolean, default: true },
    cropper: { default: () => ({}) },
    noCrop: { type: Boolean, default: false }
  }, {
    "modelValue": { required: true },
    "modelModifiers": {},
    "sort": { default: () => [] },
    "sortModifiers": {},
    "newImage": { default: () => [] },
    "newImageModifiers": {},
  }),
  emits: /*@__PURE__*/_mergeModels(["on-delete"], ["update:modelValue", "update:sort", "update:newImage"]),
  setup(__props: any, { emit: __emit }) {

const WILDCARD_IMAGE_FORMATS = ['JPG', 'PNG', 'WEBP', 'GIF']

/**
 * Ratios follow where each type is shown: avatars are round, item images sit in ~3:2 boxes (the item cards and
 * this uploader's 188×120 preview), and bill images (receipt images, digital menu backgrounds) have no set shape.
 */
const CROPPER_DEFAULTS: Record<PlaceholderType, Required<Pick<ICropperOptions, 'aspectRatio' | 'width' | 'height'>>> = {
  avatar: { aspectRatio: 1, width: 800, height: 800 },
  item: { aspectRatio: 3 / 2, width: 1200, height: 800 },
  bill: { aspectRatio: NaN, width: 1920, height: 1920 },
}


const images = _useModel<UploaderImage[]>(__props, "modelValue")
const sort = _useModel<number[]>(__props, 'sort')
const newImage = _useModel<File[]>(__props, 'newImage')
const props = __props
const emit = __emit

const fileRef = ref<QFile | null>(null)
const state = reactive<IState>({
  activeIndex: 0,
  hasError: false,
  isCropperOpen: false,
  pickedFile: null,
  previews: [],
})
let objectUrls = new Map<File, string>()

const isMultiple = computed(() => props.maxItem > 1)
const isFull = computed(() => isMultiple.value && images.value.length >= props.maxItem)
const maxFileSize = computed(() => props.maxImageSize * Math.pow(2, 20))
const previewSrc = computed(() => state.previews[state.activeIndex] ?? '')

// The hint and error texts, with {size} and {format} filled in from max-image-size and accept (e.g. 800K, JPG/PNG).
// The size is labelled in base 10 (0.8 → 800K) while the limit is base 2 (819K), so the label never overstates it.
const messages = computed(() => {
  const size = props.maxImageSize >= 1 ? `${props.maxImageSize}M` : `${Math.round(props.maxImageSize * 1000)}K`
  const format = getAcceptedFormats().join('/')
  const fill = (text: string) => text.replaceAll('{size}', size).replaceAll('{format}', format)
  return { hint: fill(props.text.imageHint), error: fill(props.text.errorMessage) }
})

const buttonLabel = computed(() => {
  if (props.placeholderType === 'avatar')
    return images.value.length ? props.text.avatar.multiple : props.text.avatar.single

  return isMultiple.value && images.value.length ? props.text.item.multiple : props.text.item.single
})

const cropperOptions = computed(() => {
  const formats = getAcceptedFormats()
  // Crops come out as JPEGs; only when nothing but PNG is accepted do they stay PNGs, keeping their transparency
  const outputType: ICropperOptions['outputType'] =
    formats.length === 1 && formats[0] === 'PNG' ? 'image/png' : 'image/jpeg'
  // Merged per option, so overriding one (e.g. only aspectRatio) keeps the defaults for the rest
  return { ...CROPPER_DEFAULTS[props.placeholderType], maxSize: maxFileSize.value, outputType, ...props.cropper }
})

/** image/jpeg → JPG, image/svg+xml → SVG, .png → PNG; a wildcard like image/* lists the common formats */
function getAcceptedFormats() {
  const formats = props.accept.split(',').flatMap((type) => {
    const format = type
      .trim()
      .replace(/^.*\/|^\./, '')
      .replace(/\+.*$/, '')
      .toUpperCase()
    if (format === '*') return WILDCARD_IMAGE_FORMATS
    return format === 'JPEG' ? 'JPG' : format
  })
  return [...new Set(formats)].filter(Boolean)
}

/** The sort numbers of the current images, without the [-1] marker */
function getImageSort() {
  return sort.value.filter((sortNumber) => sortNumber > 0)
}

function pickFile() {
  if (!isFull.value) fileRef.value?.pickFiles()
}

function onFilePicked(file: File | null) {
  if (!file || isFull.value) return
  state.hasError = false

  if (props.noCrop) addImage(file)
  else {
    state.pickedFile = file
    state.isCropperOpen = true
  }
}

function onCrop(file: File) {
  // Compression can't always reach max-image-size (e.g. a large output size), so the cropped file is checked here
  if (file.size > maxFileSize.value) {
    state.hasError = true
    return
  }
  addImage(file)
}

function addImage(file: File) {
  if (!isMultiple.value) commit([file], [SINGLE_IMAGE_SORT])
  else {
    const imageSort = getImageSort()
    const nextImages = [...images.value, file]
    commit(nextImages, [...imageSort, Math.max(0, ...imageSort) + 1])
    state.activeIndex = nextImages.length - 1
  }
}

function removeImage() {
  const index = state.activeIndex
  // Dropping the image's sort number is what deletes it on the server; single mode holds one image, so it clears
  // everything. The preview stays on the same index, which now holds the next image (or the previous one, once clamped).
  const keep = (_: unknown, i: number) => isMultiple.value && i !== index
  commit(images.value.filter(keep), getImageSort().filter(keep))
  emit('on-delete')
}

function reorderImages(from: number, to: number) {
  const imageSort = getImageSort()
  const order = images.value.map((_, i) => i)
  order.splice(to, 0, ...order.splice(from, 1))

  commit(
    order.map((i) => images.value[i]),
    order.map((i) => imageSort[i]),
  )
  state.activeIndex = order.indexOf(state.activeIndex)
}

/** Always hands the parent new arrays, so its arrays are never mutated and every v-model listener fires. */
function commit(nextImages: UploaderImage[], nextSort: number[]) {
  images.value = nextImages
  sort.value = nextImages.length ? nextSort : [NO_IMAGE_SORT]
  newImage.value = nextImages.filter((image): image is File => typeof image !== 'string')
}

// Keeps the previews in step with the images, however they changed: picked files get an object URL (revoked once
// the file is gone), and the shown index is clamped so it can't point past the end
watch(
  () => [...images.value],
  (list) => {
    const urls = new Map<File, string>()
    state.previews = list.map((image) => {
      if (typeof image === 'string') return image
      const url = objectUrls.get(image) ?? URL.createObjectURL(image)
      urls.set(image, url)
      return url
    })
    objectUrls.forEach((url, file) => {
      if (!urls.has(file)) URL.revokeObjectURL(url)
    })
    objectUrls = urls
    state.activeIndex = Math.min(state.activeIndex, Math.max(list.length - 1, 0))
  },
  { immediate: true },
)

onBeforeUnmount(() => objectUrls.forEach((url) => URL.revokeObjectURL(url)))

return (_ctx: any,_cache: any) => {
  const _component_q_img = _resolveComponent("q-img")!
  const _component_KIcon = _resolveComponent("KIcon")!
  const _component_KActionBtn = _resolveComponent("KActionBtn")!
  const _component_KCropper = _resolveComponent("KCropper")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["k-uploader column no-wrap items-center justify-center rounded-20", [`k-uploader--${__props.placeholderType}`, __props.placeholderType === 'bill' ? 'q-pa-none' : 'q-pa-lg']])
  }, [
    _createElementVNode("div", {
      class: _normalizeClass(["k-uploader__preview relative-position", `k-uploader__preview--${__props.placeholderType}`])
    }, [
      (previewSrc.value)
        ? (_openBlock(), _createBlock(_component_q_img, {
            key: 0,
            src: previewSrc.value,
            "placeholder-src": __props.placeholderSrc,
            fit: "cover",
            class: _normalizeClass(["k-uploader__image full-height non-selectable", [__props.placeholderType === 'avatar' ? 'rounded-circle' : 'rounded-12', { 'cursor-pointer': !isMultiple.value }]]),
            onClick: _cache[0] || (_cache[0] = ($event: any) => (!isMultiple.value && pickFile()))
          }, null, 8, ["src", "placeholder-src", "class"]))
        : (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass(["k-uploader__image-placeholder full-height row flex-center cursor-pointer", __props.placeholderType === 'avatar' ? 'rounded-circle' : 'rounded-12']),
            onClick: pickFile
          }, [
            _createVNode(_component_KIcon, {
              name: "add-image",
              color: "cool-gray-60",
              size: __props.placeholderType === 'bill' ? '49px' : '26px'
            }, null, 8, ["size"])
          ], 2)),
      (previewSrc.value && __props.deletable)
        ? (_openBlock(), _createElementBlock("div", {
            key: 2,
            class: "k-uploader__delete absolute row flex-center rounded-circle bg-white cursor-pointer",
            onClick: removeImage
          }, [
            _createVNode(_component_KIcon, {
              name: "trash",
              color: "error-70",
              size: "24px"
            })
          ]))
        : _createCommentVNode("", true)
    ], 2),
    _createVNode(_component_KActionBtn, {
      class: "q-mt-12 q-mb-sm",
      label: buttonLabel.value,
      disable: isFull.value,
      size: "md",
      "left-icon": "camera",
      "right-icon": "arrow-right",
      onClick: pickFile
    }, null, 8, ["label", "disable"]),
    (!state.hasError)
      ? (_openBlock(), _createElementBlock("span", {
          key: 0,
          class: _normalizeClass(["fs-13px fw-400 text-cool-gray-60 text-center", { 'q-mb-12': __props.placeholderType !== 'bill' }])
        }, _toDisplayString(messages.value.hint), 3))
      : _renderSlot(_ctx.$slots, "error", { key: 1 }, () => [
          _createElementVNode("span", {
            class: _normalizeClass(["fs-13px fw-400 text-error-100 text-center", { 'q-mb-12': __props.placeholderType !== 'bill' }])
          }, _toDisplayString(messages.value.error), 3)
        ]),
    (isMultiple.value && images.value.length > 1)
      ? (_openBlock(), _createBlock(KUploaderCarousel, {
          key: 2,
          images: state.previews,
          active: state.activeIndex,
          onSelect: _cache[1] || (_cache[1] = ($event: any) => (state.activeIndex = $event)),
          onReorder: reorderImages
        }, null, 8, ["images", "active"]))
      : _createCommentVNode("", true),
    _createVNode(_unref(QFile), {
      ref_key: "fileRef",
      ref: fileRef,
      class: "hidden",
      "model-value": null,
      accept: __props.accept,
      "max-file-size": __props.noCrop ? maxFileSize.value : undefined,
      "onUpdate:modelValue": onFilePicked,
      onRejected: _cache[2] || (_cache[2] = ($event: any) => (state.hasError = true))
    }, null, 8, ["accept", "max-file-size"]),
    (!__props.noCrop)
      ? (_openBlock(), _createBlock(_component_KCropper, _mergeProps({
          key: 3,
          modelValue: state.isCropperOpen,
          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((state.isCropperOpen) = $event))
        }, cropperOptions.value, {
          image: state.pickedFile,
          onCrop: onCrop
        }), null, 16, ["modelValue", "image"]))
      : _createCommentVNode("", true)
  ], 2))
}
}

})